<template>
  <div>
    <tr>
      <td
        v-if="additionalWidth"
        :style="{ visibility: 'hidden', width: `${additionalWidth}px`, minWidth: `${additionalWidth}px`, maxWidth: `${additionalWidth}px` }"
        class="ivu-table-column-center"
      />
      <td
        :style="{ width: `${leftWidth}px`, minWidth: `${leftWidth}px`, maxWidth: `${leftWidth}px` }"
        class="ivu-table-column-center"
      />
      <td
        v-for="(row, i) in data"
        :key="`custom-table-body-td-${i}`"
        class="ivu-table-column-center"
        :style="[{
          borderLeft: tableBodyBorderPosition[i] ? '1px solid #e8eaec': 'none',
          backgroundColor: calculateCellColor(row),
          color: column.color
            ? calculateColor(column.color, row[column.color.comparedColumn])
            : (column.background ? pickTextColorBasedOnBgColor(calculateBgColor(column.background, row[column.background.comparedColumn])): 'unset'),
          opacity: calculateOpacity(row)
        }, getColWidth(column)]"
        @mouseenter="$event => handleShowTooltip($event, row, column)"
        @mouseleave="handleCloseTooltip"
      >
        <span>
          <template v-if="numberTypes.includes(column.format)">
            {{ formatNumber(row[columnName] || column.value, column.format, column.precision) }}
          </template>
          <template v-else-if="additionalHeaderColumn && ['date'].includes(additionalHeaderColumn.format)">
            {{ $moment(row[columnName] || column.value, additionalHeaderColumn.fromFormat).format(additionalHeaderColumn.toFormat) }}
          </template>
          <template v-else>
            {{ row[columnName] || column.value }}
          </template>
        </span>
      </td>
    </tr>
    <template
      v-if="column.children"
    >
      <CustomTableTrHorizontal
        v-for="(childrenColumn, index) in Object.keys(column.children)"
        v-show="column._childrenExpand"
        :key="`custom-table-body-tr-${index}`"
        :column="{ ...column.children[childrenColumn], name: childrenColumn }"
        :data="data"
        :left-width="leftWidth"
        :col-width="colWidth"
        :col-min-width="colMinWidth"
        :min-width-factor="minWidthFactor"
        :additional-header-column="additionalHeaderColumn"
        :header-column="headerColumn"
        :additional-width="additionalWidth"
        :table-body-border-position="tableBodyBorderPosition"
        :value-change-map="valueChangeMap"
      />
    </template>
    <span
      v-show="showTooltip"
      v-transfer-dom
      data-transfer="true"
      :style="{ position: 'absolute', top: `${tooltipTop}px`, left: `${tooltipLeft}px` }"
    >
      <div
        v-if="column.tooltip"
        class="custom-tooltip"
      >
        <p v-if="additionalHeaderColumn">
          {{ additionalHeaderColumn.displayName }}: {{ formatCellByPath(selectedRow, [additionalHeaderColumn.name], additionalHeaderColumn.format, additionalHeaderColumn.fromFormat, additionalHeaderColumn.toFormat) }}
        </p>
        <p>
          {{ headerColumn.displayName }}: {{ formatCellByPath(selectedRow, [headerColumn.name], headerColumn.format, headerColumn.fromFormat, headerColumn.toFormat) }}
        </p>
        <p>
          {{ column.displayName || columnName }}: {{ formatCellByPath(selectedRow, [columnName], column.format, column.fromFormat, column.toFormat) }}
        </p>
        <template v-for="(tooltipRow, index) in column.tooltip">
          <p
            v-if="tooltipRow.value !== undefined && tooltipRow.displayName"
            :key="index"
          >
            {{ tooltipRow.displayName }}: {{ formatTooltipValue(tooltipRow.value, tooltipRow.format, tooltipRow.precision) }}
          </p>
          <p
            v-else-if="tooltipRow.column !== columnName"
            :key="index"
          >
            {{ tooltipRow.displayName || tooltipRow.column }}: {{ formatCellByPath(selectedRow, [tooltipRow.column], tooltipRow.format, tooltipRow.fromFormat, tooltipRow.toFormat) }}
          </p>
        </template>
        <p v-if="column.tooltipChangePercentage">
          Change: {{ getCellChangePercentage(selectedRow) }}
        </p>
      </div>
    </span>
  </div>
</template>

<script>
import _ from 'lodash'
import TransferDom from '@/directives/transfer-dom'
import CustomTableUtilMixin from '@/mixins/custom-table-util-mixin'

export default {
  name: 'CustomTableTrHorizontal',

  directives: {
    TransferDom
  },

  mixins: [ CustomTableUtilMixin ],

  props: {
    column: {
      type: Object,
      default: () => ({
        format: null
      })
    },
    data: {
      type: Array,
      default: () => []
    },
    leftWidth: {
      type: Number,
      default: 30
    },
    colWidth: {
      type: Number,
      default: 100
    },
    colMinWidth: {
      type: Number,
      default: 100
    },
    minWidthFactor: {
      type: Number,
      default: 1
    },
    additionalHeaderColumn: {
      type: Object,
      required: false
    },
    additionalWidth: {
      type: Number,
      default: 0
    },
    headerColumn: {
      type: Object,
      default: () => {}
    },
    tableBodyBorderPosition: {
      type: Object,
      default: () => {}
    },
    valueChangeMap: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      childrenExpand: false,
      selectedRow: {},
      showTooltip: false,
      tooltipTop: 0,
      tooltipLeft: 0,
      numberTypes: ['number', 'decimal', 'percentage', 'currency', 'currencyDecimal'],
      dateTypes: ['date']
    }
  },

  computed: {
    columnName () {
      return this.column.refColumn || this.column.name
    }
  },

  methods: {
    /**
     * Show tooltip based on mouse enter event
     * @param {Object} $event mouse event
     * @param {Object} row
     * @param {String} column
     */
    handleShowTooltip ($event, row) {
      this.selectedRow = row
      this.showTooltip = true
      this.tooltipTop = $event.pageY + 10
      this.tooltipLeft = $event.pageX + 10
    },

    /**
     * Hide tooltip when mouse leave event
     */
    handleCloseTooltip () {
      this.showTooltip = false
    },

    formatTooltipValue (value, format, precision) {
      if (this.numberTypes.includes(format)) {
        return this.formatNumber(value, format, precision)
      }

      return value
    },

    /**
     * Format cell based on path and format
     * @param {Object} row to find value based on path
     * @param {Array} path The path of the property to get.
     * @param {String} format
     * @param {String} fromFormat Original date format
     * @param {String} toFormat Transformed date format
     * @param {Number} precision Precision for number casting
     * @return {Number|Date|String} return formatted value based on types
     */
    formatCellByPath (row, path, format, fromFormat, toFormat, precision) {
      if (this.numberTypes.includes(format)) {
        return this.formatNumber(_.get(row, path, ''), format, precision)
      } else if (this.dateTypes.includes(format)) {
        return this.$moment(_.get(row, path, new Date()), fromFormat).format(toFormat)
      } else {
        return _.get(row, path, '')
      }
    },

    getCellChangePercentage (row) {
      const valueChangeObj = this.valueChangeMap[row[this.headerColumn.name]]
      let change = 0

      if (valueChangeObj && valueChangeObj.current && valueChangeObj.prior) {
        const { current, prior } = valueChangeObj

        change = (current[this.column.name] - prior[this.column.name]) / prior[this.column.name]
      }

      return this.formatNumber(change, 'percentage')
    },

    calculateCellColor (row) {
      const { colorCodeFunc, background, name } = this.column

      if (colorCodeFunc) {
        const valueChangeObj = this.valueChangeMap[row[this.headerColumn.name]]

        if (valueChangeObj && valueChangeObj.current && valueChangeObj.prior) {
          const { current, prior } = valueChangeObj

          return colorCodeFunc(current[name], prior[name])
        }
      } else if (background) {
        return this.calculateBgColor(background, row[background.comparedColumn])
      }

      return ''
    },

    /**
     * Get col width styles
     * @param {Object} column column info
     * @return {Object} style object for width setting
     */
    getColWidth (column) {
      const width = column.minWidth ? column.minWidth * this.minWidthFactor : this.colWidth

      return {
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`
      }
    },

    calculateOpacity (rowObj) {
      if (this.column.opacity && this.column.opacity.func) {
        return this.column.opacity.func(rowObj)
      }

      return null
    }
  }
}
</script>

<style scoped>
  .ivu-table-small td {
    height: 30px;
  }
</style>
